import React, { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_API_URL}`;

const VITAL_FIELDS = [
  { key: 'weight', label: 'Weight', icon: 'monitor_weight', unit: 'kg', color: '#3b82f6', bg: '#eff6ff', type: 'number', placeholder: 'e.g. 70.5' },
  { key: 'bloodPressure', label: 'Blood Pressure', icon: 'favorite', unit: 'mmHg', color: '#ef4444', bg: '#fef2f2', type: 'text', placeholder: 'e.g. 120/80' },
  { key: 'bloodGlucose', label: 'Blood Glucose', icon: 'water_drop', unit: 'mg/dL', color: '#f59e0b', bg: '#fffbeb', type: 'number', placeholder: 'e.g. 95' },
  { key: 'heartRate', label: 'Heart Rate', icon: 'cardiology', unit: 'bpm', color: '#ec4899', bg: '#fdf2f8', type: 'number', placeholder: 'e.g. 72' },
  { key: 'temperature', label: 'Temperature', icon: 'thermometer', unit: '°F', color: '#8b5cf6', bg: '#f5f3ff', type: 'number', placeholder: 'e.g. 98.6' },
];

const FEATURES = [
  { icon: 'favorite', label: 'Track\nProgress' },
  { icon: 'trending_up', label: 'Stay\nInformed' },
  { icon: 'verified_user', label: 'Prevent\nEarly' },
  { icon: 'star', label: 'Live\nHealthier' },
];

export default function MobileVitals({ user, showAdd: showAddProp, onAddClose }) {
  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({
    weight: '', bloodPressure: '', bloodGlucose: '',
    heartRate: '', temperature: '', notes: '',
    recordedAt: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { fetchVitals(); }, []); // eslint-disable-line

  useEffect(() => {
    if (showAddProp) { setShowForm(true); onAddClose && onAddClose(); }
  }, [showAddProp]); // eslint-disable-line

  const fetchVitals = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/Vitals/${user.id}`);
      if (res.ok) setVitals(await res.json());
    } catch { }
    setLoading(false);
  };

  const handleSave = async () => {
    const hasAny = form.weight || form.bloodPressure || form.bloodGlucose || form.heartRate || form.temperature;
    if (!hasAny) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/Vitals`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId: user.id,
          weight: form.weight ? parseFloat(form.weight) : null,
          bloodPressure: form.bloodPressure || null,
          bloodGlucose: form.bloodGlucose ? parseFloat(form.bloodGlucose) : null,
          heartRate: form.heartRate ? parseInt(form.heartRate) : null,
          temperature: form.temperature ? parseFloat(form.temperature) : null,
          notes: form.notes,
          recordedAt: form.recordedAt ? new Date(form.recordedAt).toISOString() : null
        })
      });
      if (res.ok) {
        setSuccess(true);
        setForm({ weight: '', bloodPressure: '', bloodGlucose: '', heartRate: '', temperature: '', notes: '', recordedAt: new Date().toISOString().split('T')[0] });
        setShowForm(false);
        fetchVitals();
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch { }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API}/api/Vitals/${id}`, { method: 'DELETE' });
      setVitals(prev => prev.filter(v => v.id !== id));
      setDeleteConfirm(null);
    } catch { }
  };

  const getLatest = (key) => {
    const v = vitals.find(v => v[key] !== null && v[key] !== undefined && v[key] !== '');
    return v ? v[key] : null;
  };

  return (
    <div className="px-4 py-4" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Success */}
      {success && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-2xl p-3 mb-4">
          <span className="material-symbols-outlined text-emerald-600 text-sm">check_circle</span>
          <p className="text-xs font-bold text-emerald-700">Vitals saved successfully!</p>
        </div>
      )}

      {/* Add Vitals Form */}
      {showForm && (
        <div className="bg-white rounded-3xl border border-gray-200 p-4 mb-4 shadow-sm">
          <h3 className="font-black text-[#12172b] text-base mb-3">Log Vitals</h3>
          <div className="space-y-3">
            {/* Date */}
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Date of Measurement</label>
              <input type="date" value={form.recordedAt} onChange={e => setForm(f => ({ ...f, recordedAt: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-2xl text-sm bg-gray-50 focus:outline-none focus:border-[#7c3aed]" />
            </div>
            {/* Vital Fields */}
            {VITAL_FIELDS.map(field => (
              <div key={field.key}>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">
                  {field.label} ({field.unit})
                </label>
                <input
                  type={field.type}
                  step="0.1"
                  placeholder={field.placeholder}
                  value={form[field.key]}
                  onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-2xl text-sm bg-gray-50 focus:outline-none focus:border-[#7c3aed]"
                />
              </div>
            ))}
            {/* Notes */}
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Notes (optional)</label>
              <textarea placeholder="Any notes or symptoms..." value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-2xl text-sm bg-gray-50 focus:outline-none focus:border-[#7c3aed] resize-none" rows={2} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)}
                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-2xl text-sm">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-3 text-white font-bold rounded-2xl text-sm disabled:opacity-50 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #d6249f)' }}>
                {saving ? '⏳ Saving...' : 'Save Vitals'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Latest Summary Cards */}
      {vitals.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Latest Readings</p>
          <div className="grid grid-cols-2 gap-2">
            {VITAL_FIELDS.map(field => {
              const val = getLatest(field.key);
              if (!val) return null;
              return (
                <div key={field.key} className="rounded-2xl p-3 border border-gray-100 shadow-sm" style={{ background: field.bg }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="material-symbols-outlined text-base" style={{ color: field.color }}>{field.icon}</span>
                    <p className="text-[10px] font-bold text-gray-500">{field.label}</p>
                  </div>
                  <p className="font-black text-lg" style={{ color: field.color }}>{val}</p>
                  <p className="text-[10px] text-gray-400">{field.unit}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History */}
      {vitals.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">History ({vitals.length})</p>
          <div className="space-y-2">
            {loading && <p className="text-center text-gray-400 text-xs py-4">Loading...</p>}
            {vitals.map(v => (
              <div key={v.id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-black text-indigo-600">
                    {new Date(v.recordedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                  <button onClick={() => setDeleteConfirm(v)}
                    className="w-8 h-8 flex items-center justify-center text-red-400 bg-red-50 hover:bg-red-100 rounded-full transition-colors">
                    <span className="material-symbols-outlined" style={{ fontSize: '17px' }}>delete</span>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {VITAL_FIELDS.map(field => (
                    v[field.key] !== null && v[field.key] !== undefined && v[field.key] !== '' && (
                      <div key={field.key} className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-sm" style={{ color: field.color }}>{field.icon}</span>
                        <div>
                          <p className="text-[10px] text-gray-400">{field.label}</p>
                          <p className="text-xs font-black text-[#0f172a]">{v[field.key]} <span className="font-normal text-gray-400 text-[10px]">{field.unit}</span></p>
                        </div>
                      </div>
                    )
                  ))}
                </div>
                {v.notes && <p className="text-xs text-gray-400 italic mt-2 pt-2 border-t border-gray-50">"{v.notes}"</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {vitals.length === 0 && !loading && !showForm && (
        <div className="bg-white rounded-3xl px-6 py-9 text-center border border-gray-100 shadow-sm mt-4">

          {/* Circular EKG illustration */}
          <div className="relative w-52 h-52 mx-auto mb-5">
            <svg viewBox="0 0 300 300" className="w-full h-full">
              <defs>
                <clipPath id="circleClip"><circle cx="150" cy="150" r="140" /></clipPath>
                <linearGradient id="ekgGrad" x1="20" y1="150" x2="280" y2="150" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#93c5fd" />
                  <stop offset="55%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#6366f1" />
                </linearGradient>
              </defs>

              <circle cx="150" cy="150" r="140" fill="#eef0fb" />

              {/* faint grid */}
              <g clipPath="url(#circleClip)" stroke="#dfe2f5" strokeWidth="1">
                {[50, 90, 130, 170, 210, 250].map(x => <line key={`v${x}`} x1={x} y1="10" x2={x} y2="290" />)}
                {[50, 90, 130, 170, 210, 250].map(y => <line key={`h${y}`} x1="10" y1={y} x2="290" y2={y} />)}
              </g>

              {/* EKG heartbeat line */}
              <path d="M35,150 L100,150 L118,120 L136,178 L156,90 L176,205 L194,150 L265,150"
                fill="none" stroke="url(#ekgGrad)" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />

              {/* decorative sparkles */}
              <g fill="#c7cdf0">
                <path d="M60,70 l3,8 l8,3 l-8,3 l-3,8 l-3,-8 l-8,-3 l8,-3 Z" />
                <path d="M245,95 l2.5,6.5 l6.5,2.5 l-6.5,2.5 l-2.5,6.5 l-2.5,-6.5 l-6.5,-2.5 l6.5,-2.5 Z" />
                <path d="M228,215 l2,5 l5,2 l-5,2 l-2,5 l-2,-5 l-5,-2 l5,-2 Z" />
                <path d="M42,225 l2,5 l5,2 l-5,2 l-2,5 l-2,-5 l-5,-2 l5,-2 Z" />
              </g>
            </svg>
          </div>

          <p className="text-[#12172b] font-black text-2xl mb-2">No vitals recorded yet</p>
          <p className="text-gray-400 text-base mb-7 leading-snug">Start tracking your health metrics<br />like BP, Heart Rate, Weight and more.</p>

          {/* Feature highlights */}
          <div className="flex items-start justify-center mb-8">
            {FEATURES.map((f, i) => (
              <React.Fragment key={f.icon}>
                {i > 0 && <div className="w-px bg-gray-200 mx-3 mt-2" style={{ height: '58px' }} />}
                <div className="flex flex-col items-center gap-2 w-16">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: '#ede9fe' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#7c3aed' }}>{f.icon}</span>
                  </div>
                  <p className="text-xs font-bold text-[#12172b] leading-tight whitespace-pre-line">{f.label}</p>
                </div>
              </React.Fragment>
            ))}
          </div>

          <button onClick={() => setShowForm(true)}
            className="w-full inline-flex items-center justify-center gap-2 py-4 text-white font-bold rounded-full text-base active:scale-95 mb-5"
            style={{ background: 'linear-gradient(90deg, #4f46e5, #a726c1, #d6249f)', boxShadow: '0 10px 25px rgba(124,58,237,0.3)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>add</span>Add Vitals
          </button>

          {/* Privacy note */}
          <div className="flex items-center gap-3 rounded-2xl p-4 text-left" style={{ background: '#f3f2fb' }}>
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#4f46e5' }}>lock</span>
            </div>
            <div>
              <p className="text-xs font-bold text-[#12172b]">Your data is private and secure</p>
              <p className="text-xs text-gray-400 mt-0.5">Only you can see your health information.</p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-end">
          <div className="w-full bg-white rounded-t-3xl p-6 shadow-2xl">
            <div className="flex flex-col items-center text-center mb-5">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mb-3">
                <span className="material-symbols-outlined text-red-500 text-3xl" style={{ fontVariationSettings: '"FILL" 1' }}>delete</span>
              </div>
              <h3 className="font-black text-[#12172b] text-lg">Delete Record?</h3>
              <p className="text-gray-400 text-sm mt-1">
                {new Date(deleteConfirm.recordedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} vitals will be deleted.
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-2xl text-sm">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="flex-1 py-3 bg-red-500 text-white font-bold rounded-2xl text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}